import { AccountId } from "./Account.schema";

export const AccountSecretCollectionName = "accountSecret";
export interface AccountSecret {
  id: AccountId;
  createdAtMS: number;
  paymentMethodsById?: Record<string, PaymentMethod>;
  // Per-club default payment method: OrgId -> the payment method id to charge for that club. NMI vault
  // tokens are per-merchant, so a method chargeable by one club may not be chargeable by another; the
  // default therefore has to be per-club. When a club has no entry here, charge selection falls back to
  // the account-wide PaymentMethod.isDefault and then the newest method the club can actually charge,
  // so existing (single-default, all-legacy) accounts behave exactly as before.
  defaultPaymentMethodIdByOrgId?: Record<string, AccountSecretPaymentMethodId>;
}

export type PaymentMethod = PaymentMethod__BankAccount | PaymentMethod__Card;
export type AccountSecretPaymentMethodId = string;

type Common = {
  id: AccountSecretPaymentMethodId;
  isDefault?: boolean;
  createdAtMS: number;
  updatedAtMS: number;
  ownerName: string;
  // NMI Customer Vault IDs for this payment method, keyed by the OrgId whose NMI merchant account
  // holds the vault record. Vault tokens are scoped to a single merchant account, and each org has
  // its own merchant account, so a card paid to multiple clubs is vaulted once per org. When a
  // vault ID exists for the charging org, the charge uses it (customer_vault_id) instead of the
  // encrypted PAN; otherwise it falls back to the legacy encrypted-number path during migration.
  nmiCustomerVaultIdByOrgId?: Record<string, string>;
};

export enum PaymentMethodType {
  card = "card",
  bank = "bank"
}

export type PaymentMethod__Card = {
  type: PaymentMethodType.card;
  brand: string;
  postalCode?: string;
  status: "active" | "expired" | "deactivated";
  expMM: string;
  expYYYY: string;
  cardNumberLast4: string;
  // Legacy self-managed vault. Optional now: cards added via Collect.js are tokenized into NMI's
  // Customer Vault and have nmiCustomerVaultIdByOrgId instead of a stored encrypted PAN.
  encryptedCardNumber?: string;
} & Common;

export type PaymentMethod__BankAccount = {
  type: PaymentMethodType.bank;
  accountNumberLast4: string;
  routingNumber: string;
  // Legacy self-managed vault. Optional now: bank accounts added via Collect.js are tokenized into
  // NMI's Customer Vault and have nmiCustomerVaultIdByOrgId instead of a stored encrypted number.
  encryptedAccountNumber?: string;
  bankAccountType: PaymentMethodBankAccountType;
} & Common;

export type PaymentMethodSnapshot =
  | (Omit<PaymentMethod__BankAccount, "encryptedAccountNumber"> & { encryptedAccountNumber: null })
  | (Omit<PaymentMethod__Card, "encryptedCardNumber"> & { encryptedCardNumber: null });

export enum PaymentMethodBankAccountType {
  checking = "checking",
  savings = "savings"
}

export type PaymentMethodOnCreation__Card = Omit<
  PaymentMethod__Card,
  keyof Pick<PaymentMethod__Card, "createdAtMS" | "updatedAtMS" | "encryptedCardNumber" | "id" | "status" | "cardNumberLast4">
> & { cardNumber: string; cvv: string };

export type PaymentMethodOnCreation__BankAccount = Omit<
  PaymentMethod__BankAccount,
  keyof Pick<PaymentMethod__BankAccount, "createdAtMS" | "updatedAtMS" | "encryptedAccountNumber" | "id" | "accountNumberLast4">
> & {
  accountNumber: string;
};

export type PaymentMethodOnCreation = PaymentMethodOnCreation__BankAccount | PaymentMethodOnCreation__Card;
